import { useCallback, useEffect, useRef } from "react";
import { debugLog, debugError } from "../common/utils/debug";
import { connectionManager } from "../services/session-connection-manager.service";
import { avatarHealthMonitor } from "../services/avatar-health-monitor.service";
import { ToolCall } from "../components/shared/ToolsPanel";
import { getUserId } from "../common/utils/userId";
import { setupConnectionEventHandlers } from "../services/session-connection-event-handlers";
import { createStateUpdater } from "../common/utils/state";
import { AVATAR_QUALITY } from "../common/constants/avatar.constants";
import { WINDOW_EVENTS } from "../common/constants/app.constants";
import { azureCustomAvatarService } from "../services/azure-custom-avatar.service";
import { speechToTextSileroService } from "../services/speech-to-text-silero.service";
import { getAzureAvatarConfigFromEnv } from "../common/utils/avatar.utils";
import { useMute } from "../context/mute";

interface useConnectionProps {
  addMessage: (message: any) => void;
  updateLastMessage: (text: string) => void;
  setSessionState: (state: any) => void;
  setAudioState: (state: any) => void;
  addToolCall: (toolCall: ToolCall) => void;
  updateToolCall: (id: string, update: Partial<ToolCall>) => void;
  resetSession: () => void;
  sessionId: string;
  setIsThinking: (thinking: boolean) => void;
}

export const useConnection = ({
  addMessage,
  updateLastMessage,
  setSessionState,
  setAudioState,
  addToolCall,
  updateToolCall,
  resetSession,
  sessionId,
  setIsThinking,
}: useConnectionProps) => {
  const { setMuted, autoUnmute } = useMute();

  // Use refs to avoid re-registering event handlers when mute functions change
  // This preserves internal state (isAvatarReady, greetingMessagesCount, etc.)
  const setMutedRef = useRef(setMuted);
  const autoUnmuteRef = useRef(autoUnmute);
  const setIsThinkingRef = useRef(setIsThinking);

  // Keep refs up to date
  useEffect(() => {
    setMutedRef.current = setMuted;
    autoUnmuteRef.current = autoUnmute;
    setIsThinkingRef.current = setIsThinking;
  }, [setMuted, autoUnmute, setIsThinking]);

  useEffect(() => {
    const cleanup = setupConnectionEventHandlers({
      addMessage,
      updateLastMessage,
      setSessionState,
      setAudioState,
      addToolCall,
      updateToolCall,
      getSessionId: () => connectionManager.getSessionId(),
      setMuted: (...args) => setMutedRef.current(...args),
      autoUnmute: () => autoUnmuteRef.current(),
      setIsThinking: (thinking) => setIsThinkingRef.current(thinking),
    });
    return cleanup;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    addMessage,
    updateLastMessage,
    setSessionState,
    setAudioState,
    addToolCall,
    updateToolCall,
    // setMuted, autoUnmute, setIsThinking accessed via refs - no need to include
  ]);
  const updateSessionState = createStateUpdater(setSessionState);

  const tearDownSession = useCallback(async () => {
    avatarHealthMonitor.stopMonitoring();

    connectionManager.endSession();

    azureCustomAvatarService.stopAvatarSession().catch(() => {});

    await Promise.all([
      connectionManager.disconnect(),
      Promise.resolve(speechToTextSileroService.cleanup()),
    ]);

    resetSession();
  }, [resetSession]);

  const startSession = useCallback(async () => {
    debugLog('[Connect] startSession: Getting user ID...');
    const userId = getUserId();
    debugLog('[Connect] startSession: User ID:', userId);

    try {
      debugLog('[Connect] startSession: Getting avatar config...');
      const avatarConfig = await getAzureAvatarConfigFromEnv();
      debugLog('[Connect] startSession: Avatar config:', avatarConfig);

      const avatarSettings = {
        avatarName: avatarConfig.avatarCharacter,
        voice: avatarConfig.voiceName,
        quality: AVATAR_QUALITY,
        language: avatarConfig.language,
        useBuiltInVoice: avatarConfig.useBuiltInVoice,
      };
      debugLog('[Connect] startSession: Avatar settings:', avatarSettings);

      debugLog('[Connect] startSession: Connecting to connection manager...');
      await connectionManager.connect(userId, avatarSettings, sessionId);
      debugLog('[Connect] startSession: Connected successfully!');

      updateSessionState({ isConnecting: false, isConnected: true });
      debugLog('[Connect] startSession: Session state updated to connected');

      avatarHealthMonitor.startMonitoring();
      debugLog('[Connect] startSession: Health monitor started');
    } catch (error) {
      debugError('[Connect] startSession: Error:', error);
      updateSessionState({ isConnecting: false, isConnected: false });
      throw error;
    }
  }, [updateSessionState, sessionId]);

  const handleConnect = useCallback(async () => {
    debugLog('[Connect] handleConnect: Starting connection process...');
    updateSessionState({ isConnecting: true });
    debugLog('[Connect] handleConnect: Set isConnecting to true');

    avatarHealthMonitor.updateActivity();
    debugLog('[Connect] handleConnect: Updated avatar health monitor activity');

    try {
      debugLog('[Connect] handleConnect: Calling startSession...');
      await startSession();
      debugLog('[Connect] handleConnect: startSession completed successfully');
    } catch (error) {
      debugError('[Connect] handleConnect: Error during connection:', error);
      updateSessionState({ isConnecting: false, isConnected: false });
    }
  }, [updateSessionState, startSession]);

  const handleDisconnect = useCallback(async () => {
    updateSessionState({ isDisconnecting: true });

    try {
      await tearDownSession();
    } catch (error) {
      window.dispatchEvent(
        new CustomEvent(WINDOW_EVENTS.CONNECTION_FATAL_ERROR, {
          detail: {
            message:
              error instanceof Error ? error.message : "Failed to disconnect",
            errorType: "disconnect_error",
          },
        })
      );
    } finally {
      updateSessionState({ isDisconnecting: false });
    }
  }, [tearDownSession, updateSessionState]);

  return {
    handleConnect,
    handleDisconnect,
  };
};
